import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { format } from 'date-fns'

import Loader from '../components/Loader'
import UrgencyBadge from '../components/tickets/UrgencyBadge'
import StatusSelect from '../components/tickets/StatusSelect'
import MessageList from '../components/tickets/MessageList'
import ReplyComposer from '../components/tickets/ReplyComposer'
import AttachmentPreview from '../components/tickets/AttachmentPreview'
import CloseTicketModal from '../components/tickets/CloseTicketModal'
import useTicket from '../hooks/useTicket'
import useTicketMessages from '../hooks/useTicketMessages'

// TicketDetailPage (design: Pages and UI components; Req 3.5, 3.6, 4.5, 5.4, 6.2)
//
// Thread view for a single ticket. The header (description, urgency, status,
// created date) comes from useTicket; admins get a StatusSelect to drive the
// lifecycle while customers see the status read-only (StatusSelect handles the
// read-only rendering itself). The message thread is loaded via
// useTicketMessages — scoped by role so customers never receive internal
// comments — and rendered chronologically by MessageList. A ReplyComposer lets
// either party append messages while the ticket is open.

// Normalize a createdAt value (Firestore Timestamp or JS Date) to a formatted
// string. Returns an empty string when the value is missing or invalid.
const formatCreatedAt = (createdAt) => {
  const date = createdAt?.toDate ? createdAt.toDate() : createdAt
  const isValidDate = date instanceof Date && !isNaN(date)
  return isValidDate ? format(date, 'PPpp') : ''
}

const TicketDetailPage = ({ role, customer, user }) => {
  const { ticketId } = useParams()
  const [showCloseModal, setShowCloseModal] = useState(false)

  const admin = role === 'admin'
  const authorEmail = user?.email

  const { data: ticket, loading } = useTicket(ticketId)
  const { data: messages } = useTicketMessages(ticketId, { admin })

  if (loading) {
    return (
      <div className='p-4'>
        <Loader />
      </div>
    )
  }

  // No ticket after load: either it doesn't exist or the user can't access it.
  if (!ticket) {
    return (
      <div className='p-4 max-w-4xl mx-auto'>
        <p className='text-gray-600'>
          Ticket not found, or you don't have access to it.
        </p>
      </div>
    )
  }

  return (
    <div className='p-4 max-w-3xl mx-auto text-left'>
      <div className='bg-white border border-gray-200 rounded-lg p-6 mb-4 shadow-sm'>
        <div className='flex items-start justify-between gap-2 mb-4'>
          <div className='flex items-center gap-3'>
            <UrgencyBadge urgency={ticket.urgency} />
            {admin && ticket.customerName && (
              <span className='text-sm font-semibold text-gray-800'>{ticket.customerName}</span>
            )}
            <span className='text-xs text-gray-500'>
              {formatCreatedAt(ticket.createdAt)}
            </span>
          </div>
          <StatusSelect ticketId={ticketId} status={ticket.status} admin={admin} />
        </div>
        {((ticket.environments || []).length > 0 || ticket.product) && (
          <div className='mb-3 flex flex-wrap gap-2 text-xs'>
            {ticket.product && (
              <span className='rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700'>
                {ticket.product}
              </span>
            )}
            {(ticket.environments || []).map((env) => (
              <span key={env} className='rounded bg-gray-100 px-2 py-0.5 font-medium text-gray-700'>
                {env}
              </span>
            ))}
          </div>
        )}

        <p className='whitespace-pre-wrap text-left text-gray-700'>{ticket.description}</p>

        {(ticket.attachments || []).length > 0 && (
          <div className='mt-3 flex flex-wrap gap-2'>
            {ticket.attachments.map((attachment, index) => (
              <AttachmentPreview key={attachment.storagePath || index} attachment={attachment} />
            ))}
          </div>
        )}

        {ticket.status === 'Closed' && ticket.closureReason && (
          <div className='mt-3 rounded border border-gray-200 bg-gray-50 p-3 text-left'>
            <p className='text-xs font-semibold uppercase text-gray-500'>Closure reason</p>
            <p className='whitespace-pre-wrap text-sm text-gray-700'>{ticket.closureReason}</p>
          </div>
        )}

        {!admin && ticket.status !== 'Closed' && (
          <div className='mt-3'>
            <button
              type='button'
              onClick={() => setShowCloseModal(true)}
              className='rounded border border-red-600 px-3 py-1 text-sm text-red-600 hover:bg-red-50'
            >
              Close ticket
            </button>
          </div>
        )}
      </div>

      <div className='bg-white border rounded-lg mb-4 shadow-sm'>
        <MessageList messages={messages} />
        <ReplyComposer
          ticketId={ticketId}
          status={ticket.status}
          admin={admin}
          authorEmail={authorEmail}
        />
      </div>

      {showCloseModal && (
        <CloseTicketModal
          ticketId={ticketId}
          onClose={() => setShowCloseModal(false)}
        />
      )}
    </div>
  )
}

export default TicketDetailPage
